import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Search, BellRing, Users, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SPECIES_FR } from "../litters/LitterCard";

const SPECIES = ["Dog", "Cat", "Bird", "Rabbit", "Reptile", "Small animal", "Livestock", "Other"];

const Stat = ({ icon: Icon, n, label, testid }) => (
  <div className="rounded-xl bg-[#F7F9FC] p-4" data-testid={testid}>
    <Icon size={16} className="text-[#F5551E]" />
    <p className="font-display text-3xl font-extrabold text-[#12234A] mt-1">{n}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

const Pick = ({ value, onChange, items, testid, w = "w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className={`h-10 rounded-xl bg-white ${w}`}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export function DemandInsights() {
  const { lang } = useLanguage();
  const fr = lang === "fr";
  const [q, setQ] = useState({ species: "Dog", breed: "", scope: "nearby", radius_km: 100 });
  const [d, setD] = useState(null);
  const [state, setState] = useState("idle");
  useEffect(() => {
    api.get("/my/litters").then((r) => { const l = (r.data.litters || r.data || [])[0]; if (l) setQ((x) => ({ ...x, species: l.species || x.species, breed: x.breed || l.breed || "" })); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (q.breed.trim().length < 2) { setD(null); setState("idle"); return undefined; }
    const ctrl = new AbortController();
    setState("loading");
    const h = setTimeout(() => api.post("/my/demand", { ...q, breed: q.breed.trim() }, { signal: ctrl.signal })
      .then((r) => { setD(r.data); setState("idle"); })
      .catch((e) => { if (e?.name !== "CanceledError") { setD(null); setState("error"); } }), 400);
    return () => { clearTimeout(h); ctrl.abort(); };
  }, [q]);
  const set = (k, v) => setQ((x) => ({ ...x, [k]: v }));
  const place = d ? d.city || d.province : "";
  const de = /^[aeiouyàâéèêîôûh]/i.test(place) ? `d'${place}` : `de ${place}`;
  const where = !d ? "" : d.scope === "canada" ? (fr ? "partout au Canada" : "across Canada") : d.scope === "radius" ? (fr ? `à moins de ${d.radius_km} km ${de}` : `within ${d.radius_km} km of ${place}`) : (fr ? "dont la zone de recherche vous inclut" : "whose search area includes you");
  return (
    <section className="mt-8 bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="demand-insights">
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center shrink-0"><TrendingUp size={20} className="text-[#F5551E]" /></span>
        <div>
          <h3 className="font-display font-bold text-[#12234A] text-lg">{fr ? "Demande pour votre race" : "Demand for your breed"}</h3>
          <p className="text-sm text-slate-500">{fr ? "Combien d'acheteurs cherchent activement cette race (annonces Recherché et alertes de portée)." : "How many shoppers are actively looking for this breed (ISO ads and litter alerts)."}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <Pick value={q.species} onChange={(v) => set("species", v)} testid="demand-species" items={SPECIES.map((s) => [s, fr ? SPECIES_FR[s] || s : s])} w="w-36" />
        <Input value={q.breed} onChange={(e) => set("breed", e.target.value)} placeholder={fr ? "Race, ex. Goldendoodle" : "Breed, e.g. Goldendoodle"} data-testid="demand-breed" className="h-10 rounded-xl w-56" />
        <Pick value={q.scope} onChange={(v) => set("scope", v)} testid="demand-scope" w="w-52"
          items={[["nearby", fr ? "À proximité (leur zone)" : "Nearby (their range)"], ["radius", fr ? "Rayon autour de moi" : "Radius around me"], ["canada", fr ? "Tout le Canada" : "All of Canada"]]} />
        {q.scope === "radius" && <Pick value={String(q.radius_km)} onChange={(v) => set("radius_km", Number(v))} testid="demand-radius" w="w-28" items={[25, 50, 100, 250].map((n) => [String(n), `${n} km`])} />}
      </div>
      {state === "error" ? (
        <p className="text-sm text-red-600 mt-5" data-testid="demand-error">{fr ? "Impossible de charger la demande. Réessayez dans un instant." : "Couldn't load demand right now. Please try again shortly."}</p>
      ) : d ? (
        <div className={`transition-opacity ${state === "loading" ? "opacity-50" : ""}`} data-testid="demand-results">
          <p className="text-sm text-[#12234A] mt-5" data-testid="demand-summary"><strong>{d.shoppers}</strong> {fr ? (d.shoppers === 1 ? "acheteur cherche" : "acheteurs cherchent") : (d.shoppers === 1 ? "shopper is looking for" : "shoppers are looking for")} <strong>{d.breed}</strong> {where}.</p>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Stat icon={Users} n={d.shoppers} label={fr ? "Acheteurs uniques" : "Unique shoppers"} testid="demand-shoppers" />
            <Stat icon={Search} n={d.iso_ads} label={fr ? "Annonces Recherché" : "ISO ads"} testid="demand-iso" />
            <Stat icon={BellRing} n={d.litter_alerts} label={fr ? "Alertes de portée" : "Litter alerts"} testid="demand-alerts" />
          </div>
          {d.recent_iso.length > 0 && (
            <ul className="mt-4 divide-y divide-slate-100" data-testid="demand-recent">
              {d.recent_iso.map((r) => (
                <li key={r.id}><Link to={`/listing/${r.id}`} className="flex items-center gap-2 py-2 text-sm hover:text-[#F5551E] transition-colors" data-testid={`demand-iso-${r.id}`}>
                  <span className="text-[10px] font-black tracking-widest bg-[#FFC233] text-[#12234A] px-1.5 py-0.5 rounded">ISO</span>
                  <span className="truncate flex-1 text-[#12234A]">{r.title}</span>
                  <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0"><MapPin size={11} /> {r.city}, {r.province}</span>
                </Link></li>
              ))}
            </ul>
          )}
        </div>
      ) : <p className="text-sm text-slate-400 mt-5" data-testid="demand-empty">{state === "loading" ? (fr ? "Chargement…" : "Loading…") : (fr ? "Entrez une race pour voir la demande." : "Enter a breed to see demand.")}</p>}
    </section>
  );
}
